import React, { useEffect, useId, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { SubMenuProps } from "./types";

const SubMenu: React.FC<SubMenuProps> = ({ title, children, closeParentMenu, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && focusedIndex !== null) {
      const menuItems = Array.from(
        subMenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []
      );
      menuItems[focusedIndex]?.focus();
    }
  }, [focusedIndex, isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);
  const handleClose = () => setIsOpen(false);

  const createClickHandler = (child: React.ReactElement) => (e: React.MouseEvent) => {
    if (child.props.target === "_blank" && child.props.href) {
      e.preventDefault();
      window.open(child.props.href, "_blank", "noopener,noreferrer");
      handleClose();
      closeParentMenu();
    } else {
      if (child.props.onClick) {
        child.props.onClick(e);
      }
      handleClose();
      closeParentMenu();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const menuItems = Array.from(
      subMenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []
    );

    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        handleToggle();
        if (!isOpen) {
          setFocusedIndex(0);
        }
        break;
      case "Escape":
        if (isOpen) {
          event.stopPropagation();
          handleClose();
          const triggerButton = subMenuRef.current?.querySelector('button[aria-controls="' + menuId + '"]');
          (triggerButton as HTMLElement)?.focus();
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) {
          handleToggle();
          setFocusedIndex(0);
        } else if (menuItems.length > 0) {
          const nextIndex = focusedIndex === null ? 0 : (focusedIndex + 1) % menuItems.length;
          setFocusedIndex(nextIndex);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        if (!isOpen) {
          handleToggle();
          setFocusedIndex(menuItems.length - 1);
        } else if (menuItems.length > 0) {
          const nextIndex = focusedIndex === null ? menuItems.length - 1 : (focusedIndex - 1 + menuItems.length) % menuItems.length;
          setFocusedIndex(nextIndex);
        }
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (subMenuRef.current && !subMenuRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="submenu-container" ref={subMenuRef}>
      <button
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={menuId}
        className="w-full text-left px-4 py-2 text-sm text-text hover:bg-surface-alt flex justify-between items-center"
        data-testid={testId}
      >
        {title}
        <ChevronRight size={16} aria-hidden="true" />
      </button>
      {/* Always render the menu container so aria-controls always resolves to an existing element */}
      <div
        id={menuId}
        className={isOpen ? "submenu open" : "submenu"}
        role="menu"
        hidden={!isOpen}
      >
        {React.Children.map(children, (child) => {
          const element = child as React.ReactElement;
          return React.cloneElement(element, {
            onClick: createClickHandler(element),
            role: "menuitem",
          });
        })}
      </div>
    </div>
  );
};

export default SubMenu;
